import type {MbscEventcalendarView} from "@mobiscroll/react";
import {daySlices} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import type {CalendarViewSettings} from "../rail/CalendarViewPicker";

export const DEFAULT_CALENDAR_VIEW_SETTINGS: CalendarViewSettings = {
    mode: "timeline",
    span: "week",
    stepMinutes: 30,
    dayPart: "all_day",
    customStartTime: "00:00",
    customEndTime: "23:59",
};

const readStoredSettings = (
    storageKey: string,
    fallback: CalendarViewSettings,
): CalendarViewSettings => {
    try {
        const stored = localStorage.getItem(storageKey);
        if (!stored) return fallback;

        const parsed = JSON.parse(stored);
        return parsed && typeof parsed === "object"
            ? {...fallback, ...parsed}
            : fallback;
    } catch {
        return fallback;
    }
};

export function useCalendarViewSettings(
    storageKey: string,
    initialValue: CalendarViewSettings = DEFAULT_CALENDAR_VIEW_SETTINGS,
) {
    const [storedState, setStoredState] = useState(() => ({
        storageKey,
        settings: readStoredSettings(storageKey, initialValue),
    }));
    const settings = storedState.settings;
    const setSettings = useCallback<Dispatch<SetStateAction<CalendarViewSettings>>>(value => {
        setStoredState(current => ({
            ...current,
            settings: typeof value === "function" ? value(current.settings) : value,
        }));
    }, []);

    useEffect(() => {
        setStoredState({
            storageKey,
            settings: readStoredSettings(storageKey, initialValue),
        });
    }, [initialValue, storageKey]);

    useEffect(() => {
        // A route can switch sectors without remounting this hook. Do not write the old
        // sector's settings into the new key during that transition render.
        if (storedState.storageKey === storageKey) {
            localStorage.setItem(storageKey, JSON.stringify(settings));
        }
    }, [settings, storageKey, storedState.storageKey]);

    const view = useMemo<MbscEventcalendarView>(() => {
        const customRangeIsValid = settings.customStartTime < settings.customEndTime;
        const [startTime, endTime] = settings.dayPart === "custom" && customRangeIsValid
            ? [settings.customStartTime, settings.customEndTime]
            : daySlices(settings.dayPart, settings.span);
        const viewSettings = {
            type: settings.span,
            timeCellStep: settings.stepMinutes,
            timeLabelStep: settings.stepMinutes,
            startTime,
            endTime,
        } as const;

        return settings.mode === "timeGrid"
            ? {schedule: viewSettings}
            : {timeline: {...viewSettings, rowHeight: "equal"}};
    }, [settings]);

    return {settings, setSettings, view};
}
